import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, FileUp, ImagePlus, Loader2, LogOut, Smartphone, Ticket } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "puertalia-admin-token";

const formatSize = (bytes) => `${(bytes / 1048576).toFixed(1).replace(".", ",")} MB`;
const formatDate = (iso) =>
    new Date(iso).toLocaleString("es-ES", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
const diaBonito = (dia) => {
    const d = new Date(`${dia}T00:00:00`);
    if (isNaN(d)) return dia;
    return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
};

const errorText = (err, fallback) =>
    typeof err.response?.data?.detail === "string" ? err.response.data.detail : fallback;

const ESTADOS = {
    pendiente: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    confirmada: "bg-green-500/10 text-green-600 dark:text-green-400",
    rechazada: "bg-red-500/10 text-red-600 dark:text-red-400",
};

const inputClass =
    "w-full rounded-xl border border-brand-navy/15 bg-white px-4 py-3 font-sans text-sm text-brand-navy outline-none transition-colors duration-200 focus:border-brand-orange dark:border-white/15 dark:bg-white/10 dark:text-white";
const labelClass =
    "mb-1.5 block font-sans text-xs font-bold uppercase tracking-wider text-brand-navy/60 dark:text-white/60";

const Panel = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [authChecked, setAuthChecked] = useState(false);
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [apk, setApk] = useState(null);
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState("");

    const [reservas, setReservas] = useState([]);
    const [resForm, setResForm] = useState({ codigo: "", nombre: "", apellidos: "", dia: "", hora: "" });
    const [resError, setResError] = useState("");
    const [resMsg, setResMsg] = useState("");
    const [creating, setCreating] = useState(false);
    const [uploadingFotos, setUploadingFotos] = useState("");

    const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

    useEffect(() => {
        if (!token) {
            setAuthChecked(true);
            return;
        }
        axios
            .get(`${API}/auth/me`, { headers: authHeaders() })
            .then((res) => setUser(res.data))
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
            })
            .finally(() => setAuthChecked(true));
    }, [token, authHeaders]);

    const loadApk = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/apk`, { headers: authHeaders() });
            setApk(res.data);
        } catch {
            /* sin APK todavía */
        }
    }, [authHeaders]);

    const loadReservas = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/reservas`, { headers: authHeaders() });
            setReservas(res.data.reservas || []);
        } catch {
            /* sin reservas todavía */
        }
    }, [authHeaders]);

    useEffect(() => {
        if (user) {
            loadApk();
            loadReservas();
        }
    }, [user, loadApk, loadReservas]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await axios.post(`${API}/auth/login`, { email, password });
            localStorage.setItem(TOKEN_KEY, res.data.token);
            setToken(res.data.token);
            setUser(res.data);
        } catch (err) {
            setError(errorText(err, "No se pudo iniciar sesión"));
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setProgress(0);
        setMessage("");
        setError("");
        try {
            const form = new FormData();
            form.append("file", file);
            const res = await axios.post(`${API}/admin/apk`, form, {
                headers: authHeaders(),
                onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
            });
            setApk(res.data);
            setFile(null);
            setMessage("APK subida correctamente. El botón de Google Play de la web ya descarga esta versión.");
        } catch (err) {
            setError(errorText(err, "No se pudo subir el archivo"));
        } finally {
            setUploading(false);
        }
    };

    const handleCrearReserva = async (e) => {
        e.preventDefault();
        setResError("");
        setResMsg("");
        setCreating(true);
        try {
            await axios.post(`${API}/admin/reservas`, resForm, { headers: authHeaders() });
            setResMsg(`Reserva creada. Dale este código al cliente: ${resForm.codigo.trim().toUpperCase()}`);
            setResForm({ codigo: "", nombre: "", apellidos: "", dia: "", hora: "" });
            loadReservas();
        } catch (err) {
            setResError(errorText(err, "No se pudo crear la reserva"));
        } finally {
            setCreating(false);
        }
    };

    const handleFotos = async (codigo, files) => {
        if (!files?.length) return;
        setUploadingFotos(codigo);
        setResError("");
        setResMsg("");
        try {
            for (const f of Array.from(files)) {
                const form = new FormData();
                form.append("file", f);
                await axios.post(`${API}/admin/reservas/${codigo}/fotos`, form, { headers: authHeaders() });
            }
            setResMsg(`Fotos añadidas a la reserva ${codigo}.`);
            loadReservas();
        } catch (err) {
            setResError(errorText(err, "No se pudieron subir las fotos"));
        } finally {
            setUploadingFotos("");
        }
    };

    const handleLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
        setApk(null);
        setFile(null);
        setReservas([]);
    };

    return (
        <div className="min-h-screen bg-brand-cream dark:bg-brand-night" data-testid="panel-page">
            <header className="border-b border-white/10 bg-brand-ink">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
                    <div className="flex items-center gap-3">
                        <Logo light />
                        <span className="rounded-full bg-brand-orange/15 px-3 py-1 font-sans text-xs font-bold text-brand-orange">
                            Panel PAMI
                        </span>
                    </div>
                    <Link
                        to="/"
                        data-testid="panel-back-btn"
                        className="flex items-center gap-2 font-sans text-sm font-bold text-white/70 transition-colors duration-200 hover:text-white"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Ver web
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-md px-5 py-14">
                {!authChecked ? (
                    <div className="flex justify-center py-20" data-testid="panel-loading">
                        <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
                    </div>
                ) : !user ? (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="rounded-2xl border border-brand-navy/10 bg-white p-8 shadow-xl dark:border-white/10 dark:bg-white/5"
                    >
                        <h1 className="font-sans text-2xl font-extrabold tracking-tight text-brand-navy dark:text-white">
                            Acceso al panel
                        </h1>
                        <p className="mt-2 text-sm text-brand-navy/60 dark:text-white/60">
                            Solo para el equipo de Puertalia Portal.
                        </p>
                        <form onSubmit={handleLogin} className="mt-7 space-y-4" data-testid="panel-login-form">
                            <div>
                                <label htmlFor="panel-email" className={labelClass}>
                                    Correo
                                </label>
                                <input
                                    id="panel-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="panel-email-input"
                                    className={inputClass}
                                    placeholder="tu@correo.com"
                                    autoComplete="username"
                                />
                            </div>
                            <div>
                                <label htmlFor="panel-password" className={labelClass}>
                                    Contraseña
                                </label>
                                <input
                                    id="panel-password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="panel-password-input"
                                    className={inputClass}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                />
                            </div>
                            {error && (
                                <p className="rounded-xl bg-red-500/10 px-4 py-3 font-sans text-sm font-bold text-red-600 dark:text-red-400" data-testid="panel-error">
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="panel-login-submit"
                                className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3.5 font-sans text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-orangedark disabled:opacity-60"
                            >
                                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                                Entrar
                            </button>
                        </form>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="space-y-6"
                        data-testid="panel-dashboard"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="font-sans text-2xl font-extrabold tracking-tight text-brand-navy dark:text-white">
                                    Panel PAMI
                                </h1>
                                <p className="mt-1 text-xs text-brand-navy/50 dark:text-white/50">{user.email}</p>
                            </div>
                            <button
                                onClick={handleLogout}
                                data-testid="panel-logout-btn"
                                className="flex items-center gap-2 rounded-full border border-brand-navy/15 px-4 py-2 font-sans text-xs font-bold text-brand-navy transition-colors duration-200 hover:bg-brand-navy/5 dark:border-white/15 dark:text-white dark:hover:bg-white/10"
                            >
                                <LogOut className="h-3.5 w-3.5" />
                                Salir
                            </button>
                        </div>

                        <div className="rounded-2xl border border-brand-navy/10 bg-white p-6 shadow-lg dark:border-white/10 dark:bg-white/5" data-testid="panel-apk-info">
                            <div className="flex items-start gap-4">
                                <span className="rounded-xl bg-brand-orange/10 p-3 text-brand-orange">
                                    <Smartphone className="h-6 w-6" />
                                </span>
                                <div className="min-w-0">
                                    {apk?.available ? (
                                        <>
                                            <p className="truncate font-sans text-sm font-bold text-brand-navy dark:text-white" data-testid="panel-apk-name">
                                                {apk.original_filename}
                                            </p>
                                            <p className="mt-1 text-xs text-brand-navy/60 dark:text-white/60" data-testid="panel-apk-meta">
                                                {formatSize(apk.size)} · subida el {formatDate(apk.uploaded_at)}
                                            </p>
                                            <p className="mt-2 flex items-center gap-1.5 font-sans text-xs font-bold text-green-600 dark:text-green-400">
                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                                Activa: el botón de Google Play descarga esta APK
                                            </p>
                                        </>
                                    ) : (
                                        <p className="text-sm text-brand-navy/60 dark:text-white/60">
                                            Aún no has subido ninguna APK. El botón de Google Play de la web no descarga nada todavía.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-brand-navy/10 bg-white p-6 shadow-lg dark:border-white/10 dark:bg-white/5">
                            <h2 className="font-sans text-base font-bold text-brand-navy dark:text-white">
                                {apk?.available ? "Sustituir por una nueva versión" : "Subir la APK"}
                            </h2>
                            <label
                                htmlFor="panel-apk-input"
                                className="mt-4 flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-brand-navy/20 px-4 py-8 text-center transition-colors duration-200 hover:border-brand-orange dark:border-white/20"
                                data-testid="panel-upload-dropzone"
                            >
                                <FileUp className="h-8 w-8 text-brand-orange" />
                                <span className="font-sans text-sm font-bold text-brand-navy dark:text-white" data-testid="panel-selected-file">
                                    {file ? file.name : "Toca para elegir el archivo .apk"}
                                </span>
                                <span className="text-xs text-brand-navy/50 dark:text-white/50">
                                    {file ? formatSize(file.size) : "Máximo 60 MB"}
                                </span>
                                <input
                                    id="panel-apk-input"
                                    type="file"
                                    accept=".apk,application/vnd.android.package-archive"
                                    className="hidden"
                                    data-testid="panel-upload-input"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                />
                            </label>

                            {uploading && (
                                <div className="mt-4" data-testid="panel-upload-progress">
                                    <div className="h-2 overflow-hidden rounded-full bg-brand-navy/10 dark:bg-white/10">
                                        <div className="h-full rounded-full bg-brand-orange transition-[width] duration-200" style={{ width: `${progress}%` }} />
                                    </div>
                                    <p className="mt-1.5 text-center font-sans text-xs font-bold text-brand-navy/60 dark:text-white/60">{progress}%</p>
                                </div>
                            )}

                            {error && (
                                <p className="mt-4 rounded-xl bg-red-500/10 px-4 py-3 font-sans text-sm font-bold text-red-600 dark:text-red-400" data-testid="panel-upload-error">
                                    {error}
                                </p>
                            )}
                            {message && (
                                <p className="mt-4 rounded-xl bg-green-500/10 px-4 py-3 font-sans text-sm font-bold text-green-600 dark:text-green-400" data-testid="panel-upload-success">
                                    {message}
                                </p>
                            )}

                            <button
                                onClick={handleUpload}
                                disabled={!file || uploading}
                                data-testid="panel-upload-btn"
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3.5 font-sans text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-orangedark disabled:opacity-50"
                            >
                                {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
                                {uploading ? "Subiendo…" : "Subir APK"}
                            </button>
                            <p className="mt-3 text-center text-xs text-brand-navy/50 dark:text-white/50">
                                Al subirla, el botón de Google Play de la web descargará esta APK automáticamente.
                            </p>
                        </div>

                        <div className="rounded-2xl border border-brand-navy/10 bg-white p-6 shadow-lg dark:border-white/10 dark:bg-white/5" data-testid="panel-reservas">
                            <div className="flex items-center gap-3">
                                <span className="rounded-xl bg-brand-navy/10 p-3 text-brand-navy dark:bg-white/10 dark:text-white">
                                    <Ticket className="h-6 w-6" />
                                </span>
                                <h2 className="font-sans text-base font-bold text-brand-navy dark:text-white">
                                    Reservas de visita
                                </h2>
                            </div>

                            <form onSubmit={handleCrearReserva} className="mt-5 space-y-3" data-testid="reserva-create-form">
                                <div>
                                    <label htmlFor="res-codigo" className={labelClass}>Código de reserva</label>
                                    <input
                                        id="res-codigo"
                                        type="text"
                                        required
                                        value={resForm.codigo}
                                        onChange={(e) => setResForm({ ...resForm, codigo: e.target.value.toUpperCase() })}
                                        data-testid="reserva-codigo-input"
                                        className={`${inputClass} font-mono uppercase tracking-widest`}
                                        placeholder="PTL-1234"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label htmlFor="res-nombre" className={labelClass}>Nombre</label>
                                        <input
                                            id="res-nombre"
                                            type="text"
                                            required
                                            value={resForm.nombre}
                                            onChange={(e) => setResForm({ ...resForm, nombre: e.target.value })}
                                            data-testid="reserva-nombre-input"
                                            className={inputClass}
                                            placeholder="María"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="res-apellidos" className={labelClass}>Apellidos</label>
                                        <input
                                            id="res-apellidos"
                                            type="text"
                                            required
                                            value={resForm.apellidos}
                                            onChange={(e) => setResForm({ ...resForm, apellidos: e.target.value })}
                                            data-testid="reserva-apellidos-input"
                                            className={inputClass}
                                            placeholder="García"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label htmlFor="res-dia" className={labelClass}>Día</label>
                                        <input
                                            id="res-dia"
                                            type="date"
                                            required
                                            value={resForm.dia}
                                            onChange={(e) => setResForm({ ...resForm, dia: e.target.value })}
                                            data-testid="reserva-dia-input"
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="res-hora" className={labelClass}>Hora</label>
                                        <input
                                            id="res-hora"
                                            type="time"
                                            required
                                            value={resForm.hora}
                                            onChange={(e) => setResForm({ ...resForm, hora: e.target.value })}
                                            data-testid="reserva-hora-input"
                                            className={inputClass}
                                        />
                                    </div>
                                </div>
                                {resError && (
                                    <p className="rounded-xl bg-red-500/10 px-4 py-3 font-sans text-sm font-bold text-red-600 dark:text-red-400" data-testid="reserva-create-error">
                                        {resError}
                                    </p>
                                )}
                                {resMsg && (
                                    <p className="rounded-xl bg-green-500/10 px-4 py-3 font-sans text-sm font-bold text-green-600 dark:text-green-400" data-testid="reserva-create-success">
                                        {resMsg}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    disabled={creating}
                                    data-testid="reserva-create-btn"
                                    className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-navy px-5 py-3.5 font-sans text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-ink disabled:opacity-50"
                                >
                                    {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Crear reserva
                                </button>
                            </form>

                            {reservas.length > 0 && (
                                <ul className="mt-6 space-y-3" data-testid="reservas-list">
                                    {reservas.map((r) => (
                                        <li
                                            key={r.codigo}
                                            className="rounded-xl border border-brand-navy/10 p-4 dark:border-white/10"
                                            data-testid={`reserva-item-${r.codigo}`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-mono text-sm font-bold tracking-widest text-brand-navy dark:text-white">
                                                    {r.codigo}
                                                </span>
                                                <span className={`rounded-full px-3 py-1 font-sans text-[11px] font-bold ${ESTADOS[r.estado] || ESTADOS.pendiente}`}>
                                                    {r.estado}
                                                </span>
                                            </div>
                                            <p className="mt-1.5 text-xs text-brand-navy/70 dark:text-white/60">
                                                {r.nombre} {r.apellidos} · {diaBonito(r.dia)} · {r.hora}
                                            </p>
                                            <div className="mt-3 flex items-center justify-between gap-2">
                                                <span className="text-xs text-brand-navy/50 dark:text-white/50">
                                                    {r.fotos} foto{r.fotos === 1 ? "" : "s"}
                                                </span>
                                                <label
                                                    className="flex cursor-pointer items-center gap-1.5 rounded-full border border-brand-navy/15 px-3 py-1.5 font-sans text-[11px] font-bold text-brand-navy transition-colors duration-200 hover:bg-brand-navy/5 dark:border-white/15 dark:text-white dark:hover:bg-white/10"
                                                    data-testid={`reserva-fotos-btn-${r.codigo}`}
                                                >
                                                    {uploadingFotos === r.codigo ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : (
                                                        <ImagePlus className="h-3.5 w-3.5" />
                                                    )}
                                                    Añadir fotos
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        multiple
                                                        className="hidden"
                                                        data-testid={`reserva-fotos-input-${r.codigo}`}
                                                        onChange={(e) => {
                                                            handleFotos(r.codigo, e.target.files);
                                                            e.target.value = "";
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <p className="mt-4 text-center text-xs text-brand-navy/50 dark:text-white/50">
                                El cliente escribe su código en la página /visita y confirma o rechaza de 9:00 a 19:00.
                            </p>
                        </div>
                    </motion.div>
                )}
            </main>
        </div>
    );
};

export default Panel;
